import { __ } from "@wordpress/i18n";
import {
	useBlockProps,
	InspectorControls,
	InnerBlocks,
	PanelColorSettings,
} from "@wordpress/block-editor";
import {
	PanelBody,
	RangeControl,
	ToggleControl,
	TextareaControl,
} from "@wordpress/components";
import { useLayoutEffect, useRef } from "@wordpress/element";
import { useSelect } from "@wordpress/data";

import { Swiper } from "swiper";
import { Autoplay, Navigation, Pagination } from "swiper/modules";

import "./style.scss";

// A template for starting point.
const SLIDE_TEMPLATE = [
	[
		"core/group",
		{
			style: { color: { text: "#ffffff", background: "#4a4a4a" } },
			layout: { type: "flex", justifyContent: "center" },
		},
		[["core/heading", { level: 2, placeholder: "Slide 1 Title" }]],
	],
	[
		"core/group",
		{
			style: { color: { text: "#ffffff", background: "#808080" } },
			layout: { type: "flex", justifyContent: "center" },
		},
		[["core/heading", { level: 2, placeholder: "Slide 2 Title" }]],
	],
];

export default function Edit({ attributes, setAttributes, clientId }) {
	const {
		slidesPerView,
		slidesPerViewMobile,
		slidesPerViewTablet,
		spaceBetween,
		speed,
		autoplay,
		showPagination,
		carouselHeight,
		arrowBgColor,
		arrowIconColor,
		arrowIconSize,
		arrowBgSize,
		prevIconSVG,
		nextIconSVG,
		iconRadius,
	} = attributes;
	const blockProps = useBlockProps();
	const swiperRef = useRef(null);

	// Styles for the editor preview buttons
	const arrowStyles = {
		backgroundColor: arrowBgColor,
		width: `${arrowBgSize}px`,
		height: `${arrowBgSize}px`,
		borderRadius: `${iconRadius}px`,
	};
	const iconStyles = {
		fontSize: `${arrowIconSize}px`,
		color: arrowIconColor,
	};

	// Get the number of inner blocks to update Swiper when a slide is added/removed
	const { innerBlockCount } = useSelect(
		(select) => ({
			innerBlockCount: select("core/block-editor").getBlockCount(clientId),
		}),
		[clientId],
	);

	useLayoutEffect(() => {
		if (!swiperRef.current || innerBlockCount === 0) return;

		// Delay to ensure InnerBlocks have rendered in the DOM
		setTimeout(() => {
			const container = swiperRef.current.querySelector(
				".block-editor-inner-blocks",
			);
			if (!container) return;

			container.classList.add("swiper-container");

			const wrapper = container.querySelector(
				".block-editor-block-list__layout",
			);
			if (wrapper) {
				wrapper.classList.add("swiper-wrapper");

				Array.from(wrapper.children).forEach((child) => {
					child.classList.add("swiper-slide");
				});
			}

			const swiperConfig = {
				loop: innerBlockCount > slidesPerView,
				slidesPerView,
				breakpoints: {
					320: {
						slidesPerView: slidesPerViewMobile,
					},
					768: {
						slidesPerView: slidesPerViewTablet,
					},
					1024: {
						slidesPerView: slidesPerView,
					},
				},
				spaceBetween,
				modules: [Autoplay, Navigation, Pagination],
				autoplay: autoplay ? { delay: speed } : false,
				navigation: {
					nextEl: swiperRef.current.querySelector(".swiper-button-next"),
					prevEl: swiperRef.current.querySelector(".swiper-button-prev"),
				},
			};

			if (showPagination) {
				swiperConfig.pagination = {
					el: swiperRef.current.querySelector(".swiper-pagination"),
					clickable: true,
				};
			}

			const swiperInstance = new Swiper(container, swiperConfig);

			return () => swiperInstance.destroy();
		}, 0);
	}, [
		innerBlockCount,
		slidesPerView,
		slidesPerViewMobile,
		slidesPerViewTablet,
		spaceBetween,
		speed,
		autoplay,
		showPagination,
	]);

	return (
		<div {...blockProps} style={{ height: `${carouselHeight}px` }}>
			<InspectorControls>
				<PanelBody title={__("Slider Settings", "swift-fse-blocks")}>
					<RangeControl
						label={__("Carousel Height (px)", "swift-fse-blocks")}
						value={carouselHeight}
						onChange={(val) => setAttributes({ carouselHeight: val })}
						min={150}
						max={1000}
						step={10}
					/>
					<RangeControl
						label={__("Slides per view", "swift-fse-blocks")}
						value={slidesPerView}
						onChange={(val) => setAttributes({ slidesPerView: val })}
						min={1}
						max={6}
					/>
					<RangeControl
						label={__("Slides Per View (Desktop)", "swift-fse-blocks")}
						value={slidesPerView}
						onChange={(value) => setAttributes({ slidesPerView: value })}
						min={1}
						max={6}
					/>
					<RangeControl
						label={__("Slides Per View (Tablet)", "swift-fse-blocks")}
						value={slidesPerViewTablet}
						onChange={(value) => setAttributes({ slidesPerViewTablet: value })}
						min={1}
						max={4}
					/>
					<RangeControl
						label={__("Slides Per View (Mobile)", "swift-fse-blocks")}
						value={slidesPerViewMobile}
						onChange={(value) => setAttributes({ slidesPerViewMobile: value })}
						min={1}
						max={3}
					/>
					<RangeControl
						label={__("Space Between Slides (px)", "swift-fse-blocks")}
						value={spaceBetween}
						onChange={(val) => setAttributes({ spaceBetween: val })}
						min={0}
						max={100}
						step={5}
					/>
					<ToggleControl
						label={__("Autoplay", "swift-fse-blocks")}
						checked={autoplay}
						onChange={(val) => setAttributes({ autoplay: val })}
					/>
					{autoplay && (
						<RangeControl
							label={__("Transition speed (ms)", "swift-fse-blocks")}
							value={speed}
							onChange={(val) => setAttributes({ speed: val })}
							min={100}
							max={10000}
							step={100}
						/>
					)}

					<ToggleControl
						label={__("Show Pagination", "swift-fse-blocks")}
						checked={showPagination}
						onChange={(val) => setAttributes({ showPagination: val })}
					/>

					{/* <SelectControl
						label={__("Arrow Style", "swift-fse-blocks")}
						value={arrowStyle}
						options={[
							{ label: "Default", value: "default" },
							{ label: "Minimal", value: "minimal" },
							{ label: "Bold", value: "bold" },
						]}
						onChange={(val) => setAttributes({ arrowStyle: val })}
					/> */}
				</PanelBody>
				<PanelBody title={__("Navigation Arrows", "swift-fse-blocks")}>
					<RangeControl
						label={__("Arrow Background Size", "swift-fse-blocks")}
						value={arrowBgSize}
						onChange={(value) => setAttributes({ arrowBgSize: value })}
						min={20}
						max={80}
					/>
					<RangeControl
						label={__("Arrow Icon Size", "swift-fse-blocks")}
						value={arrowIconSize}
						onChange={(value) => setAttributes({ arrowIconSize: value })}
						min={10}
						max={50}
					/>
					<RangeControl
						label={__("Icon Radius", "swift-fse-blocks")}
						value={iconRadius}
						onChange={(value) => setAttributes({ iconRadius: value })}
						min={1}
						max={100}
					/>
					<PanelColorSettings
						title={__("Arrow Colors", "swift-fse-blocks")}
						colorSettings={[
							{
								value: arrowBgColor,
								onChange: (value) => setAttributes({ arrowBgColor: value }),
								label: __("Background Color", "swift-fse-blocks"),
							},
							{
								value: arrowIconColor,
								onChange: (value) => setAttributes({ arrowIconColor: value }),
								label: __("Icon Color", "swift-fse-blocks"),
							},
						]}
					/>
				</PanelBody>
				<PanelBody
					title={__("Custom Icons (SVG)", "swift-fse-blocks")}
					initialOpen={false}
				>
					<TextareaControl
						label={__("Previous Icon SVG", "swift-fse-blocks")}
						value={prevIconSVG}
						onChange={(value) => setAttributes({ prevIconSVG: value })}
						help={__(
							'Paste your full SVG code here. Ensure it uses `fill="currentColor"` to inherit the icon color.',
							"swift-fse-blocks",
						)}
					/>
					<TextareaControl
						label={__("Next Icon SVG", "swift-fse-blocks")}
						value={nextIconSVG}
						onChange={(value) => setAttributes({ nextIconSVG: value })}
					/>
				</PanelBody>
			</InspectorControls>

			<div
				ref={swiperRef}
				className="content-slider swiper-container"
				data-slides={slidesPerView}
				data-space-between={spaceBetween}
				data-speed={speed}
				data-autoplay={autoplay ? "true" : "false"}
				data-pagination={showPagination ? "true" : "false"}
				style={{ height: `${carouselHeight}px` }}
			>
				<InnerBlocks
					// template={SLIDE_TEMPLATE}
					// Optionally, only allow specific blocks as slides
					allowedBlocks="all"
				/>
				<div className="swiper-button-prev" style={arrowStyles}>
					<span
						style={iconStyles}
						dangerouslySetInnerHTML={{ __html: prevIconSVG }}
					/>
				</div>
				<div className="swiper-button-next" style={arrowStyles}>
					<span
						style={iconStyles}
						dangerouslySetInnerHTML={{ __html: nextIconSVG }}
					/>
				</div>
				{showPagination && <div className="swiper-pagination"></div>}
			</div>
		</div>
	);
}
